import { updater } from "utils/updater.js";
import { setLocalStorage } from "utils/globalLocalStorage.js";

/**
 * Sidebar API factory.
 *
 */
export function uploaderFactory({ state, setState }) {
  // shorthand for setState(produce(draft => {...}))
  const updateState = updater(setState);

  const setOpenUploader = (bool) => {
    updateState((draft) => {
      draft.openUploader = bool;
    });
  };
  const getOpenUploader = () => state.openUploader;

  const setFilesPreview = (files) => {
    updateState((draft) => {
      draft.filesPreview = { ...state.filesPreview, ...files };
    });
  };

  const getFilesPreview = () => state.filesPreview;

  const setToken = (token) => {
    updateState((draft) => {
      draft.token = token;
    });
  };

  const getToken = () => state.token;

  const setIsJMPay = (bool) => {
    updateState((draft) => {
      draft.isJMPay = bool;
    });
  };

  const getIsJMPay = () => state.isJMPay;

  const setUploadId = (uploadId) =>
    updateState((draft) => {
      draft.uploadId = uploadId;
    });

  const getUploadId = () => state.uploadId;

  const setMailId = (mailId) => {
    updateState((draft) => {
      draft.mailId = mailId;
    });
  };

  const getMailId = () => state.mailId;

  const setRecipientEmail = (recipient) => {
    recipient && setLocalStorage("sendViaLink", "email");
    updateState((draft) => {
      draft.recipientData = recipient;
    });
  };

  const getRecipientEmail = () => state.recipientData;

  const setUploading = (bool) =>
    updateState((draft) => {
      draft.uploading = bool;
    });

  const getUploading = () => state.uploading;

  const setSize = (size) => {
    updateState((draft) => {
      draft.size = size ? draft.size + size : 0;
    });
  };

  const getSize = () => state.size;

  const setSaved = (bool) => {
    updateState((draft) => {
      draft.saved = bool;
    });
  };

  const getSaved = () => state.saved;

  const setExpireAfter = (expireAfter) => {
    updateState((draft) => {
      draft.expireAfter = expireAfter;
    });
  };

  const getExpireAfter = () => state.expireAfter;

  const setVerifiedEmail = (email) => {
    updateState((draft) => {
      draft.verifiedEmail = email;
    });
  };

  const getVerifiedEmail = () => state.verifiedEmail;

  const setMailVerifying = (bool) => {
    updateState((draft) => {
      draft.mailVerifying = bool;
    });
  };

  const getMailVerifying = () => state.mailVerifying;

  const setMailVerifyingProcess = ({ time, sessionId }) => {
    updateState((draft) => {
      draft.verifyingProcess = {
        time,
        sessionId,
      };
    });
  };

  const getMailVerifyingProcess = () => state.verifyingProcess;

  const setSpamProtection = ({ link, mail, captcha }) => {
    updateState((draft) => {
      draft.spamProtection = {
        link,
        mail,
        captcha,
      };
    });
  };

  const getSpamProtection = () => state.spamProtection;

  const setJumboCode = (code) => {
    updateState((draft) => {
      draft.jumboCode = code;
    });
  };

  const getJumboCode = () => state.jumboCode;

  const getHideSubmit = () => state.hideSubmit;

  const setHideSubmit = (bool) => {
    updateState((draft) => {
      draft.hideSubmit = bool;
    });
  };

  // UPLOADER API FUNCTIONS

  const setUploader = (uploaderAPI) => {
    updateState((draft) => {
      draft.uploader = uploaderAPI;
    });
  };

  const getUploader = () => state.uploader;

  const getIsUppy = () => state.uplodaerAPI === "uppy";

  const setIsUppy = (bool) => {
    updateState((draft) => {
      draft.uplodaerAPI = bool ? "uppy" : "fine";
    });
  };

  const uploaderAddFile = async (file, disableVideoDurationCalc) => {
    if (state.uplodaerAPI === "uppy") {
      state.uploader?.addFile(file);
    } else {
      if (file.data?.size === 0) {
        return;
      }
      const fileToAdd = new File([file.data], file.name, { type: file.type });
      fileToAdd.meta = {
        ...file.meta,
      };
      fileToAdd.extension = file.name.split(".").pop();
      if (file.type.includes("video") && !disableVideoDurationCalc) {
        const video = document.createElement("video");
        video.preload = "metadata";

        video.src = URL.createObjectURL(file.data);

        video.onloadedmetadata = function () {
          const duration = Math.ceil(video.duration);
          // add video duration to the file meta and state
          fileToAdd.meta = {
            ...fileToAdd.meta,
            duration: duration,
          };
          addVideoDuration(duration);
          // Clean up
          window.URL.revokeObjectURL(video.src);
        };
      }
      state.uploader?.addFiles(fileToAdd);
    }
  };

  const addVideoDuration = (duration) => {
    updateState((draft) => {
      draft.videoDuration += duration;
    });
  };

  const setTooManyVideos = (bool) => {
    updateState((draft) => {
      draft.tooManyVideos = bool;
    });
  };

  const getTooManyVideos = () => state.tooManyVideos;

  const removeVideoDuration = (duration) => {
    updateState((draft) => {
      draft.videoDuration -= duration;
    });
  };

  const setAllowedVideoDuration = (duration) => {
    updateState((draft) => {
      draft.allowedVideoDuration = duration;
    });
  };

  const getVideoDuration = () => state.videoDuration;

  const getIsVideodurationExceeded = () => {
    return state.videoDuration > state.allowedVideoDuration
      ? state.allowedVideoDuration || "0"
      : false;
  };

  const getFileVideoDuration = (fileId) => {
    if (state.uplodaerAPI === "uppy") {
      return state.uploader?.getFile(fileId).meta.duration;
    } else {
      return state.uploader?.getFile(fileId).meta.duration;
    }
  };

  const uploaderRemoveFile = (fileId) => {
    if (state.uplodaerAPI === "uppy") {
      state.uploader?.removeFile(fileId);
    } else {
      getFileVideoDuration(fileId) &&
        removeVideoDuration(getFileVideoDuration(fileId));
      state.uploader?.cancel(fileId);
    }
  };

  const uploaderRetryUpload = (fileId) => {
    if (state.uplodaerAPI === "uppy") {
      state.uploader?.retryUpload(fileId);
    } else {
      state.uploader?.retry(fileId);
    }
  };

  const uploaderCancelAll = () => {
    if (state.uplodaerAPI === "uppy") {
      state.uploader?.cancelAll();
    } else {
      updateState((draft) => {
        draft.size = 0;
        draft.videoDuration = 0;
      });
      state.uploader?.reset();
    }
  };

  const uploaderPauseAll = () => {
    if (state.uplodaerAPI === "uppy") {
      state.uploader?.pauseAll();
    } else {
      state.uploader?.getUploads().forEach((upload) => {
        state.uploader?.pauseUpload(upload.id);
      });
    }
  };

  const uploaderResumeAll = () => {
    if (state.uplodaerAPI === "uppy") {
      state.uploader?.resumeAll();
    } else {
      state.uploader?.getUploads().forEach((upload) => {
        state.uploader?.continueUpload(upload.id);
      });
      setTimeout(() => {
        state.uploader?.uploadStoredFiles();
      }, 3000);
    }
  };

  const uploaderGetFiles = () => {
    if (state.uplodaerAPI === "uppy") {
      return state.uploader?.getFiles() || [];
    } else {
      return (
        state.uploader
          ?.getUploads()
          .filter((file) => !["canceled", "rejected"].includes(file.status)) ||
        []
      );
    }
  };

  const uplaoderStartUpload = () => {
    if (state.uplodaerAPI === "uppy") {
      state.uploader?.upload();
    } else {
      state.uploader?.uploadStoredFiles();
    }
  };

  const uploaderSetFileMeta = (fileId, meta) => {
    if (state.uplodaerAPI === "uppy") {
      state.uploader?.setFileMeta(fileId, meta);
    } else {
      const newMeta = {
        ...state.uploader?.getFile(fileId).meta,
        ...meta,
      };
      state.uploader?.setParams({ meta: newMeta }, fileId);
    }
  };

  const uploaderSetUuid = (fileId, isEmbed, uniqueId) => {
    state.uploader?.setUuid(
      fileId,
      (isEmbed ? "embed/" : "files/") + state.mailId + "/" + uniqueId
    );
  };

  const uploaderSetMeta = async ({
    getUniqueId,
    addWaterMark = false,
    addVideoWatermark = false,
    isEmbed = false,
    filesOwnerEmail,
    attemts = 0,
    callback,
  }) => {
    // JMFORMS
    if (state.uplodaerAPI === "uppy") {
      state.uploader?.setMeta({
        mailId: state.mailId,
        addWatermarkToThumbnail: addWaterMark,
        addVideoWatermark: addVideoWatermark,
        isEmbed,
        filesOwnerEmail,
      });
    } else {
      const files = state.uploader?.getUploads();

      if (attemts > 3) {
        uploaderCancelAll();
        callback && callback();
        return;
      }

      let currentAttemts = attemts;

      // checking if files exist and uploader is ready and mailId exists
      if (!files || files.length === 0 || !state.uploader || !state.mailId) {
        currentAttemts++;

        setTimeout(async () => {
          await uploaderSetMeta({
            addWaterMark,
            addVideoWatermark,
            isEmbed,
            filesOwnerEmail,
            callback,
            attemts: currentAttemts,
          });
        }, 500);
      } else {
        files.forEach((file) => {
          uploaderSetUuid(file.id, isEmbed, getUniqueId());
          const qqpath = file.file.meta.relativePath
            ? file.file.meta.relativePath
                .replace(/\\\\/g, "/")
                .split(file.name)[0]
            : null;

          const params = {
            addWatermarkToThumbnail: addWaterMark,
            addVideoWatermark: addVideoWatermark,
            imageratio: 1,
            size: file.size,
            folderId: undefined,
            qqpath,
            isEmbed,
            filesOwnerEmail,
          };

          if (!qqpath) {
            delete params.qqpath;
          }
          if (!filesOwnerEmail) {
            delete params.filesOwnerEmail;
          }

          state.uploader?.setParams(params, file.id);
        });

        return true;
      }
    }
  };

  return {
    // UPLOADER API
    getUploader,
    setUploader,
    getIsUppy,
    setIsUppy,
    uploaderAddFile,
    uploaderRemoveFile,
    uploaderRetryUpload,
    uploaderCancelAll,
    uploaderPauseAll,
    uploaderResumeAll,
    uploaderGetFiles,
    uplaoderStartUpload,
    uploaderSetFileMeta,
    uploaderSetMeta,
    uploaderSetUuid,
    // LOGIC METHODS
    setUploadId,
    getUploadId,
    setOpenUploader,
    getOpenUploader,
    setTooManyVideos,
    getTooManyVideos,
    getIsVideodurationExceeded,
    setAllowedVideoDuration,
    getVideoDuration,
    setMailId,
    getMailId,
    setUploading,
    getUploading,
    setRecipientEmail,
    getRecipientEmail,
    setToken,
    getToken,
    setIsJMPay,
    getIsJMPay,
    setSize,
    getSize,
    setSaved,
    getSaved,
    setExpireAfter,
    getExpireAfter,
    setVerifiedEmail,
    getVerifiedEmail,
    setFilesPreview,
    getFilesPreview,
    setMailVerifying,
    getMailVerifying,
    setMailVerifyingProcess,
    getMailVerifyingProcess,
    setSpamProtection,
    getSpamProtection,
    setJumboCode,
    getJumboCode,
    getHideSubmit,
    setHideSubmit,
  };
}

export default uploaderFactory;
